import { useState } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import { useUser } from 'builder/hooks/useUser'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'

import { TRANSLATION } from '../../../constants'
import { Service } from '../../../../types'

export type First90DaysPlanInfo = {
  badge?: string
  header: string
  subHeader: string
  bookCoachButton: string
  ninetyDaysButton: string
  ninetyDaysButtonMobile: string
  closeModal: string
  bookCoachModal: string
}

type Step = {
  completed?: boolean
  score?: number
}

export type Steps = {
  ninety_days_page?: Step
  career_coaching?: Step
}

type First90DaysPlanConfig = {
  text: First90DaysPlanInfo
  status: 'initial' | 'completed' | 'progress'
  steps: Steps
  handleCloseModal: () => void
}

const localStorageKey = 'first_90_days_seen_modal'

export const useFirst90DaysPlanConfig = (service: Service): First90DaysPlanConfig => {
  const user = useUser()
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const [isModalOpen, setIsModalOpen] = useState(!localStorage.getItem(localStorageKey))

  const getStep = (stepName: string) => {
    return service.data.steps?.find(step => step.step_name === stepName)
  }

  const getSteps = () => {
    const ninetyDaysPage = getStep('page_seen')
    const bookCoach = getStep('career_coaching')
    const completed = user?.uiStates?.first90DaysPlanSeen || ninetyDaysPage?.completed

    return {
      ninety_days_page: { completed, score: ninetyDaysPage?.target_score },
      career_coaching: { completed: bookCoach?.completed, score: bookCoach?.target_score },
    }
  }

  const getStatus = () => {
    if (isModalOpen) return 'initial'
    if (service.progress === 100) return 'completed'
    return 'progress'
  }

  const getBookCoachButton = () => {
    const steps = getSteps()
    const key = steps.career_coaching?.completed ? 'book_coach_again' : 'book_coach'
    return i18n.t(`${TRANSLATION}.first_90_days_plan.progress.${key}`)
  }

  const getNinetyDaysButton = () => {
    const steps = getSteps()
    const key = steps.ninety_days_page?.completed ? 'see_plan' : 'review_plan'
    return i18n.t(`${TRANSLATION}.first_90_days_plan.progress.${isPhone ? 'see_plan_mobile' : key}`)
  }

  const getText = () => {
    const status = getStatus()
    const key = status === 'completed' ? 'completed' : `first_90_days_plan.${status}`

    return {
      ...(i18n.t(`${TRANSLATION}.${key}`) as unknown as First90DaysPlanInfo),
      bookCoachModal: i18n.t(`${TRANSLATION}.first_90_days_plan.${status}.book_coach`),
      bookCoachButton: getBookCoachButton(),
      ninetyDaysButton: getNinetyDaysButton(),
    }
  }

  const handleCloseModal = () => {
    localStorage.setItem(localStorageKey, 'true')
    setIsModalOpen(false)
  }

  return {
    text: getText(),
    steps: getSteps(),
    status: getStatus(),
    handleCloseModal,
  }
}
